import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useSearchParams } from "react-router-dom";
import { endpoints } from "../api.js";
import { oidcSignIn } from "../auth.js";
import { Icon } from "../components/Icon.jsx";
import { ErrorBanner, FullPageLoader, Spinner } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocalePath } from "../i18n/hooks.js";
import { AuthShell } from "../layouts.jsx";
import { useSession } from "../session.jsx";

function safeNext(next, fallback) {
  // Only allow same-origin absolute paths.
  return next && next.startsWith("/") && !next.startsWith("//") ? next : fallback;
}

export default function Login() {
  useDocumentTitle("login.title");
  const intl = useIntl();
  const lp = useLocalePath();
  const errMsg = useErrorMessage();
  const [params] = useSearchParams();
  const { status, authConfig, refreshMe, error: bootError, boot } = useSession();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const next = safeNext(params.get("next"), lp("/"));

  if (status === "loading") return <FullPageLoader />;
  if (status === "ready") return <Navigate to={next} replace />;

  const devLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await endpoints.devLogin(email.trim());
      await refreshMe();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const ssoLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      await oidcSignIn(authConfig, next);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div className="card auth-card">
        <div className="auth-eyebrow">
          <Icon name="shield" size={15} />
          <FormattedMessage id="login.eyebrow" />
        </div>
        <h1 className="h1">
          <FormattedMessage
            id="login.heading"
            values={{ accent: (chunks) => <span className="grad-text">{chunks}</span> }}
          />
        </h1>
        <p className="muted">
          <FormattedMessage id="login.subtitle" />
        </p>

        {status === "error" ? <ErrorBanner message={errMsg(bootError)} onRetry={boot} /> : null}
        <ErrorBanner message={error ? errMsg(error) : null} />

        {authConfig?.mode === "oidc" ? (
          <button type="button" className="btn btn-primary btn-block" onClick={ssoLogin} disabled={busy}>
            {busy ? <Spinner size={16} /> : <Icon name="lock" size={16} />}
            <FormattedMessage id="login.sso" />
          </button>
        ) : authConfig?.mode === "dev" ? (
          <form className="form" onSubmit={devLogin}>
            <div className="banner banner-warn small" role="note">
              <Icon name="alert" size={16} />
              <span className="banner-text">
                <FormattedMessage id="login.devNotice" />
              </span>
            </div>
            <label className="field">
              <span className="field-label">
                <FormattedMessage id="login.email" />
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={intl.formatMessage({ id: "login.emailPlaceholder" })}
              />
            </label>
            <button type="submit" className="btn btn-primary btn-block" disabled={busy || !email}>
              {busy ? <Spinner size={16} /> : null}
              <FormattedMessage id="login.submit" />
            </button>
          </form>
        ) : null}
      </div>
    </AuthShell>
  );
}
